import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Flag, Gift } from "@phosphor-icons/react";
import { BulaLogo } from "@/components/BulaLogo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref")?.toUpperCase() || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(name, email, password);
    setLoading(false);
    if (r.ok) {
      // If they arrived via a referral link, redeem the code immediately.
      if (refCode) {
        try {
          const rr = await api.post("/referrals/redeem", { code: refCode });
          toast.success(`Welcome! +${rr.data.days_added} days of Bula Pro from ${rr.data.referrer_name} 🎉`);
        } catch {
          toast.success(`Bula ${name.split(" ")[0]}! Account created.`);
        }
      } else {
        toast.success(`Bula ${name.split(" ")[0]}! Account created.`);
      }
      navigate("/dashboard");
    } else toast.error(r.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8F7F3]">
      <div className="relative hidden md:block">
        <img src="https://images.unsplash.com/photo-1592937238247-cd0090e02f65?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F2537]/90 to-[#0F2537]/30" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="register-logo">
            <BulaLogo size={36} />
            <span className="font-heading font-black text-lg">Bula Golf</span>
          </Link>
          <div>
            <h2 className="font-heading font-black text-4xl tracking-tighter">Join Fiji's golf community.</h2>
            <p className="mt-3 text-white/80 max-w-sm">Track every card, watch your WHS handicap drop, brag a little (or a lot).</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md card-default p-8 rise" data-testid="register-form">
          <span className="label-eyebrow">Create account</span>
          <h1 className="font-heading font-black text-3xl mt-1">Start your card</h1>
          <p className="text-[#0F2537]/70 text-sm mt-1">It's free, forever.</p>

          {refCode && (
            <div className="mt-4 p-3 rounded-xl bg-[#E05D3A]/10 border border-[#E05D3A]/20 flex items-center gap-2" data-testid="register-ref-banner">
              <Gift size={16} weight="fill" className="text-[#E05D3A] shrink-0" />
              <span className="text-xs font-bold text-[#0F2537]">
                Referral code <span className="font-mono">{refCode}</span> applied — you'll get <b>1 month of Bula Pro free</b> on sign-up.
              </span>
            </div>
          )}

          <label className="block mt-6 text-sm font-semibold">Full name</label>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />

          <label className="block mt-4 text-sm font-semibold">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />

          <label className="block mt-4 text-sm font-semibold">Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
          <p className="text-xs text-[#0F2537]/60 mt-1">At least 6 characters.</p>

          <button type="submit" disabled={loading} data-testid="register-submit-btn"
            className="mt-6 w-full py-3 rounded-xl bg-[#E05D3A] text-white font-bold hover:brightness-110 transition disabled:opacity-60">
            {loading ? "Creating account…" : "Create account"}
          </button>

          <p className="mt-5 text-sm text-[#0F2537]/70 text-center">
            Have an account?{" "}
            <Link to="/login" data-testid="register-to-login-link" className="text-[#0F2537] font-semibold underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
